// src/pages/membres/ListeMembres.jsx
import React, { useState, useEffect } from 'react';
import {
    Row, Col, Card, Spinner, Modal, Alert, Button, Badge,
    Table, Pagination, Form, InputGroup
} from 'react-bootstrap';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { formatDate } from '../../utils/formatters';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import { ROLES } from '../../data/Menu';
import { COLORS } from '../../constants/colors';

export default function ListeMembres({ filtre }) {
    const navigate = useNavigate();
    const location = useLocation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filterLoading, setFilterLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState(filtre || '');
    const [genreFilter, setGenreFilter] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [membreToDelete, setMembreToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const [permissions, setPermissions] = useState({
        canView: false,
        canAdd: false,
        canEdit: false,
        canDelete: false,
        canFilter: false,
        canExport: false
    });

    useEffect(() => {
        if (filtre) {
            setRoleFilter(filtre);
            setCurrentPage(1);
        }
    }, [filtre, location.pathname]);

    useEffect(() => {
        fetchMembres();
    }, [currentPage, searchTerm, roleFilter, genreFilter, location.pathname]);

    // ===== Permissions =====
    const hasPermission = (action) => {
        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) return false;
            const user = JSON.parse(userStr);
            const userRole = user.role || user.type_utilisateur;
            if (userRole === ROLES.SUPER_ADMIN) return true;

            const permissionsMap = {
                'view_members': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DIACRE, ROLES.DIACONESSE, ROLES.DP, ROLES.ACCUEIL, ROLES.NOUVEAU, ROLES.MEMBRE],
                'add_member': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR],
                'edit_member': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR],
                'delete_member': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR],
                'view_filters': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR, ROLES.ANCIEN, ROLES.DIACRE, ROLES.DIACONESSE, ROLES.DP, ROLES.ACCUEIL, ROLES.NOUVEAU, ROLES.MEMBRE],
                'export_members': [ROLES.SUPER_ADMIN, ROLES.ADMIN, ROLES.PASTEUR]
            };
            return permissionsMap[action]?.includes(userRole) || false;
        } catch (e) {
            console.error('Erreur permission:', e);
            return false;
        }
    };

    useEffect(() => {
        setPermissions({
            canView: hasPermission('view_members'),
            canAdd: hasPermission('add_member'),
            canEdit: hasPermission('edit_member'),
            canDelete: hasPermission('delete_member'),
            canFilter: hasPermission('view_filters'),
            canExport: hasPermission('export_members')
        });
    }, []);

    // ===== API =====
    const fetchMembres = async () => {
        if (data) setFilterLoading(true);
        else setLoading(true);

        try {
            const userStr = localStorage.getItem('user');
            if (!userStr) {
                navigate('/pages/signin2');
                return;
            }
            const user = JSON.parse(userStr);
            const userId = user.id || user.id_utilisateur;

            let url = `https://josephsbusiness.com/api/membres.php?user_id=${userId}&page=${currentPage}&limit=${itemsPerPage}`;
            if (searchTerm) url += `&search=${encodeURIComponent(searchTerm)}`;
            if (roleFilter) url += `&role=${encodeURIComponent(roleFilter)}`;
            if (genreFilter) url += `&genre=${encodeURIComponent(genreFilter)}`;

            const response = await fetch(url, { headers: { 'Accept': 'application/json' } });
            const result = await response.json();

            if (result.success) {
                setData(result);
                setError(null);
            } else {
                setError(result.message || 'Erreur de chargement');
            }
        } catch (err) {
            console.error('Erreur:', err);
            setError(err.message);
        } finally {
            setLoading(false);
            setFilterLoading(false);
        }
    };

    // ===== Handlers =====
    const handleRoleFilterChange = (e) => {
        setRoleFilter(e.target.value);
        setCurrentPage(1);
    };
    const handleGenreFilterChange = (e) => {
        setGenreFilter(e.target.value);
        setCurrentPage(1);
    };
    const handleSearch = (e) => {
        e.preventDefault();
        setCurrentPage(1);
    };
    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const getRoleBadgeColor = (role) => {
        const colors = {
            'Pasteur': 'danger',
            'Ancien': 'info',
            'Diacre': 'success',
            'Diaconesse': 'success',
            'Jeune': 'warning',
            'Nouveau': 'primary'
        };
        return colors[role] || 'secondary';
    };

    const getRoleIcon = (role) => {
        const icons = {
            'Pasteur': 'ri-user-star-line',
            'Ancien': 'ri-user-settings-line',
            'Diacre': 'ri-user-heart-line',
            'Diaconesse': 'ri-user-heart-line',
            'Jeune': 'ri-user-smile-line',
            'Nouveau': 'ri-user-add-line'
        };
        return icons[role] || 'ri-user-line';
    };

    const getGenreIcon = (genre) => {
        const icons = {
            'Homme': 'ri-men-line',
            'Femme': 'ri-women-line',
            'Enfant': 'ri-baby-line'
        };
        return icons[genre] || 'ri-user-line';
    };

    const getGenreBadgeColor = (genre) => {
        const colors = {
            'Homme': 'info',
            'Femme': 'success',
            'Enfant': 'warning'
        };
        return colors[genre] || 'secondary';
    };

    const handleDeleteClick = (membre) => {
        setMembreToDelete(membre);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!membreToDelete) return;
        setDeleting(true);
        try {
            const userStr = localStorage.getItem('user');
            const user = JSON.parse(userStr);
            const userId = user.id || user.id_utilisateur;

            const response = await fetch(`https://josephsbusiness.com/api/membres.php?user_id=${userId}&id=${membreToDelete.id}`, {
                method: 'DELETE',
                headers: { 'Accept': 'application/json' }
            });
            const result = await response.json();

            if (result.success) {
                setShowDeleteModal(false);
                setMembreToDelete(null);
                await fetchMembres();
                setSuccessMessage('Membre supprimé avec succès');
                setShowSuccessMessage(true);
                setTimeout(() => setShowSuccessMessage(false), 3000);
            } else {
                alert(result.message || 'Erreur lors de la suppression');
            }
        } catch (err) {
            alert('Erreur: ' + err.message);
        } finally {
            setDeleting(false);
        }
    };

    const getTitle = () => {
        if (filtre) {
            const titles = {
                'Pasteur': 'Pasteurs',
                'Ancien': 'Anciens',
                'Diacre': 'Diacres',
                'Diaconesse': 'Diaconesses',
                'Jeune': 'Jeunes',
                'Nouveau': 'Nouveaux'
            };
            return titles[filtre] || 'Membres';
        }
        return 'Membres';
    };

    // ===== Loading / Error =====
    if (loading && !data) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
                            <h5 className="fw-bold mt-3" style={{ color: COLORS.primaryDark }}>Chargement des {getTitle().toLowerCase()}</h5>
                            <p className="text-muted">Récupération des données en cours...</p>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error && !data) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchMembres}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    const stats = data?.stats || {};
    const membres = data?.membres || [];
    const pagination = data?.pagination || { total: 0, pages: 1 };

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">

                    {/* ===== EN‑TÊTE MODERNE (comme Recommandations) ===== */}
                    <div className="header-modern mb-4 fade-in">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-group-line"></i>
                            </div>
                            <div>
                                <h5 className="gradient-text">{getTitle()}</h5>
                                <div className="d-flex flex-wrap gap-2">
                                    <span className="badge-modern">
                                        <i className="ri-bar-chart-line"></i> Total {stats.total || 0}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                        <i className="ri-user-star-line"></i> Pasteurs {stats.pasteurs || 0}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.info}15`, color: COLORS.info }}>
                                        <i className="ri-user-settings-line"></i> Anciens {stats.anciens || 0}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.success}15`, color: COLORS.success }}>
                                        <i className="ri-user-heart-line"></i> Diacres {stats.diacres || 0}
                                    </span>
                                    <span className="badge-modern" style={{ background: `${COLORS.warning}15`, color: COLORS.warning }}>
                                        <i className="ri-user-smile-line"></i> Jeunes {stats.jeunes || 0}
                                    </span>
                                    <span className="badge-modern" style={{ background: `rgba(13,202,240,0.15)`, color: '#0dcaf0' }}>
                                        <i className="ri-user-add-line"></i> Nouveaux {stats.nouveaux || 0}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                           
                            {permissions.canAdd && (
                                <Button size="sm" className="btn-modern-primary" as={Link} to="/membres/ajouter">
                                    <i className="ri-add-line"></i> Nouveau {filtre ? getTitle().slice(0, -1) : 'membre'}
                                </Button>
                            )}
                            {/* {filtre && permissions.canView && (
                                <Button size="sm" className="btn-modern-outline" onClick={() => navigate('/membres/liste')}>
                                    <i className="ri-close-line"></i> Voir tous
                                </Button>
                            )} */}
                        </div>
                    </div>

                    {/* ===== KPI CARDS (comme Clients) ===== */}
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="2">
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon"><i className="ri-group-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Total</span>
                                    <h3 className="kpi-value">{stats.total || 0}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="2">
                            <div className="kpi-card kpi-danger">
                                <div className="kpi-icon"><i className="ri-user-star-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Pasteurs</span>
                                    <h3 className="kpi-value">{stats.pasteurs || 0}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="2">
                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon"><i className="ri-user-settings-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Anciens</span>
                                    <h3 className="kpi-value">{stats.anciens || 0}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="2">
                            <div className="kpi-card kpi-success">
                                <div className="kpi-icon"><i className="ri-user-heart-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Diacres</span>
                                    <h3 className="kpi-value">{stats.diacres || 0}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="2">
                            <div className="kpi-card kpi-warning">
                                <div className="kpi-icon"><i className="ri-user-smile-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Jeunes</span>
                                    <h3 className="kpi-value">{stats.jeunes || 0}</h3>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="2">
                            <div className="kpi-card kpi-primary" style={{ borderLeftColor: '#0dcaf0' }}>
                                <div className="kpi-icon" style={{ background: '#e2f3f5', color: '#0dcaf0' }}><i className="ri-user-add-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Nouveaux</span>
                                    <h3 className="kpi-value">{stats.nouveaux || 0}</h3>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* ===== FILTRES (fond primaryDark, comme Clients) ===== */}
                    {showFilters && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4" style={{ background: COLORS.primaryDark }}>
                            <Card.Body className="py-3 px-4">
                                <Form onSubmit={handleSearch}>
                                    <Row className="align-items-center g-3">
                                        <Col md="5">
                                            <div className="position-relative">
                                                <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-white-50 fs-14"></i>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Rechercher (nom, prénom, matricule)"
                                                    value={searchTerm}
                                                    onChange={(e) => setSearchTerm(e.target.value)}
                                                    className="ps-5 rounded-pill"
                                                    style={{ background: 'rgba(255,255,255,0.9)' }}
                                                />
                                            </div>
                                        </Col>
                                        <Col md="3">
                                            <Form.Select
                                                value={roleFilter}
                                                onChange={handleRoleFilterChange}
                                                className="rounded-pill px-3"
                                                style={{ background: 'rgba(255,255,255,0.9)' }}
                                            >
                                                <option value="">Tous les rôles</option>
                                                <option value="Pasteur">Pasteur</option>
                                                <option value="Ancien">Ancien</option>
                                                <option value="Diacre">Diacre</option>
                                                <option value="Diaconesse">Diaconesse</option>
                                                <option value="Jeune">Jeune</option>
                                                <option value="Nouveau">Nouveau</option>
                                            </Form.Select>
                                        </Col>
                                        <Col md="3">
                                            <Form.Select
                                                value={genreFilter}
                                                onChange={handleGenreFilterChange}
                                                className="rounded-pill px-3"
                                                style={{ background: 'rgba(255,255,255,0.9)' }}
                                            >
                                                <option value="">Tous les genres</option>
                                                <option value="Homme">Homme</option>
                                                <option value="Femme">Femme</option>
                                                <option value="Enfant">Enfant</option>
                                            </Form.Select>
                                        </Col>
                                        <Col md="1">
                                            <Button
                                                type="submit"
                                                variant="light"
                                                size="sm"
                                                className="rounded-pill w-100"
                                                disabled={filterLoading}
                                            >
                                                {filterLoading ? <Spinner size="sm" /> : <i className="ri-check-line"></i>}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            </Card.Body>
                        </Card>
                    )}

                    {/* ===== MESSAGE DE SUCCÈS ===== */}
                    {showSuccessMessage && (
                        <Alert
                            variant="success"
                            className="border-0 shadow-sm rounded-3 mb-3"
                            onClose={() => setShowSuccessMessage(false)}
                            dismissible
                        >
                            <div className="d-flex align-items-center">
                                <i className="ri-checkbox-circle-line fs-4 me-2" style={{ color: COLORS.success }}></i>
                                <span>{successMessage}</span>
                            </div>
                        </Alert>
                    )}

                    {/* ===== TABLEAU (en‑tête secondary) ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="border-0 py-3" style={{ background: COLORS.secondary }}>
                            <div className="d-flex justify-content-between align-items-center">
                                <div className="d-flex align-items-center">
                                    <i className="ri-list-check text-white me-2 fs-5"></i>
                                    <span className="fw-semibold text-white">Liste des {getTitle().toLowerCase()}</span>
                                    <Badge bg="light" text="dark" className="ms-2 rounded-pill">
                                        {pagination.total} au total
                                    </Badge>
                                </div>

                                <div className="d-flex align-items-right">
                                 <Button size="sm" className="btn-modern-outline bg-warning mr-2" style={{marginRight:'2px'}} onClick={() => setShowFilters(!showFilters)}>
                                <i className="ri-filter-line"></i> {showFilters ? 'Masquer' : 'Filtres'}
                            </Button>
                            <Button size="sm" className="btn-modern-outline" onClick={fetchMembres} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'fa-spin' : ''}`}></i> Actualiser
                            </Button>
                                {filterLoading && (
                                    <Spinner animation="border" size="sm" variant="light" />
                                )}

                                </div>

                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Matricule</th>
                                            <th>Membre</th>
                                            <th>Rôle</th>
                                            <th>Genre</th>
                                            <th>Contact</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {membres.length > 0 ? (
                                            membres.map((membre) => (
                                                <tr key={membre.id}>
                                                    <td className="ps-4">
                                                        <span className="reference-badge">{membre.matricule}</span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="member-avatar">
                                                                {membre.prenom?.charAt(0)}{membre.nom?.charAt(0)}
                                                            </div>
                                                            <div className="fw-semibold">{membre.prenom} {membre.nom}</div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Badge
                                                            bg={getRoleBadgeColor(membre.role)}
                                                            className="rounded-pill"
                                                        >
                                                            <i className={getRoleIcon(membre.role) + ' me-1'}></i>
                                                            {membre.role}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <Badge
                                                            bg={getGenreBadgeColor(membre.gendre)}
                                                            className="rounded-pill"
                                                        >
                                                            <i className={getGenreIcon(membre.gendre) + ' me-1'}></i>
                                                            {membre.gendre}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        {membre.contact ? (
                                                            <span><i className="ri-phone-line me-1 text-muted"></i>{membre.contact}</span>
                                                        ) : (
                                                            <span className="text-muted">-</span>
                                                        )}
                                                    </td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            {permissions.canView && (
                                                                <button
                                                                    className="action-icon action-icon-primary"
                                                                    onClick={() => navigate(`/membres/${membre.id}`)}
                                                                    title="Voir détails"
                                                                >
                                                                    <i className="ri-eye-line"></i>
                                                                </button>
                                                            )}
                                                            {permissions.canEdit && (
                                                                <button
                                                                    className="action-icon action-icon-primary"
                                                                    onClick={() => navigate(`/membres/modifier/${membre.id}`)}
                                                                    title="Modifier"
                                                                >
                                                                    <i className="ri-pencil-line"></i>
                                                                </button>
                                                            )}
                                                            {permissions.canDelete && (
                                                                <button
                                                                    className="action-icon action-icon-danger"
                                                                    onClick={() => handleDeleteClick(membre)}
                                                                    title="Supprimer"
                                                                >
                                                                    <i className="ri-delete-bin-line"></i>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className="text-center py-5">
                                                    <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                                    <h6 className="mb-1">Aucun membre trouvé</h6>
                                                    <p className="text-muted small mb-0">Essayez de modifier vos critères de recherche</p>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== PAGINATION ===== */}
                    {pagination.pages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-4 px-3">
                            <div className="text-muted small">
                                Page {currentPage} sur {pagination.pages}
                            </div>
                            <Pagination className="mb-0">
                                <Pagination.First onClick={() => handlePageChange(1)} disabled={currentPage === 1} />
                                <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
                                {[...Array(Math.min(5, pagination.pages))].map((_, i) => {
                                    let pageNum;
                                    if (pagination.pages <= 5) pageNum = i + 1;
                                    else if (currentPage <= 3) pageNum = i + 1;
                                    else if (currentPage >= pagination.pages - 2) pageNum = pagination.pages - 4 + i;
                                    else pageNum = currentPage - 2 + i;
                                    return (
                                        <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => handlePageChange(pageNum)}>
                                            {pageNum}
                                        </Pagination.Item>
                                    );
                                })}
                                <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.pages} />
                                <Pagination.Last onClick={() => handlePageChange(pagination.pages)} disabled={currentPage === pagination.pages} />
                            </Pagination>
                        </div>
                    )}

                    <Footer />
                </div>
            </div>

          {/* ===== MODAL DE SUPPRESSION ===== */}
<Modal 
  show={showDeleteModal} 
  onHide={() => setShowDeleteModal(false)} 
  centered 
  className="delete-modal-modern"
>
  <Modal.Header 
    closeButton 
    className="border-0 pb-0" 
    style={{ 
      background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`, 
      borderTopLeftRadius: '4px', 
      borderTopRightRadius: '4px' 
    }}
  >
    <Modal.Title className="w-100 text-center">
      <div className="delete-icon-wrapper mb-2">
        <div className="delete-icon-circle" style={{
          width: '70px',
          height: '70px',
          borderRadius: '4px',
          background: 'rgba(255,255,255,0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto'
        }}>
          <i className="ri-delete-bin-line" style={{ fontSize: '32px', color: 'white' }}></i>
        </div>
      </div>
      <h5 className="fw-bold text-white mb-0">Confirmer la suppression</h5>
      <p className="text-white-50 small mb-0">Cette action est irréversible</p>
    </Modal.Title>
  </Modal.Header>

  <Modal.Body className="pt-4 px-4">
    {membreToDelete && (
      <>
        <p className="text-center text-muted mb-3">
          Êtes-vous sûr de vouloir supprimer définitivement ce membre ?
        </p>

        {/* Carte d'information du membre */}
        <div className="p-3 mb-3" style={{
          background: '#f8fafc',
          borderLeft: `3px solid ${COLORS.primary}`,
          borderRadius: '4px'
        }}>
          <div className="d-flex align-items-center gap-3">
            <div className="member-avatar" style={{
              width: '48px',
              height: '48px',
              borderRadius: '4px',
              background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: '700',
              fontSize: '1.2rem',
              flexShrink: 0
            }}>
              {membreToDelete.prenom?.charAt(0)}{membreToDelete.nom?.charAt(0)}
            </div>
            <div>
              <div className="fw-bold" style={{ color: COLORS.primaryDark }}>
                {membreToDelete.prenom} {membreToDelete.nom}
              </div>
              <div className="text-muted small">
                <i className="ri-fingerprint-line me-1"></i>
                Matricule: {membreToDelete.matricule}
              </div>
              <Badge bg={getRoleBadgeColor(membreToDelete.role)} className="rounded-pill mt-1">
                {membreToDelete.role}
              </Badge>
            </div>
          </div>
        </div>

        {/* Avertissement */}
        <div style={{
          background: `${COLORS.danger}10`,
          border: `1px solid ${COLORS.danger}30`,
          borderRadius: '4px',
          padding: '0.8rem 1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.8rem'
        }}>
          <i className="ri-alert-line" style={{ color: COLORS.danger, fontSize: '1.3rem' }}></i>
          <span className="small text-muted">
            Toutes les données associées à ce membre seront définitivement supprimées.
          </span>
        </div>
      </>
    )}
  </Modal.Body>

  <Modal.Footer className="border-0 pt-0 pb-4 px-4 justify-content-center gap-2">
    <Button 
      variant="light" 
      onClick={() => setShowDeleteModal(false)} 
      disabled={deleting}
      className="px-4"
      style={{
        borderRadius: '4px',
        padding: '0.5rem 1.5rem',
        border: '1px solid #d1d5db',
        color: '#4b5563',
        fontWeight: '500',
        fontSize: '0.85rem',
        transition: 'all 0.2s',
        background: 'white'
      }}
    >
      Annuler
    </Button>
    <Button
      variant="danger"
      onClick={handleDeleteConfirm}
      disabled={deleting}
      className="px-4"
      style={{
        background: `linear-gradient(135deg, ${COLORS.danger}, #dc2626)`,
        border: 'none',
        borderRadius: '4px',
        padding: '0.5rem 1.5rem',
        fontWeight: '500',
        fontSize: '0.85rem',
        boxShadow: '0 4px 10px rgba(239, 68, 68, 0.25)',
        transition: 'all 0.2s',
        color: 'white'
      }}
      onMouseEnter={(e) => {
        e.target.style.transform = 'translateY(-2px)';
        e.target.style.boxShadow = '0 8px 16px rgba(239, 68, 68, 0.35)';
      }}
      onMouseLeave={(e) => {
        e.target.style.transform = 'translateY(0)';
        e.target.style.boxShadow = '0 4px 10px rgba(239, 68, 68, 0.25)';
      }}
    >
      {deleting ? (
        <><Spinner size="sm" className="me-1" /> Suppression...</>
      ) : (
        <><i className="ri-delete-bin-line me-2"></i>Oui, supprimer</>
      )}
    </Button>
  </Modal.Footer>
</Modal>

            {/* ===== STYLES (KPI + actions, identiques à Clients) ===== */}
            <style>{`
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: ${COLORS.primary}; }
                .kpi-success { border-left-color: ${COLORS.success}; }
                .kpi-danger { border-left-color: ${COLORS.danger}; }
                .kpi-warning { border-left-color: ${COLORS.warning}; }
                .kpi-info { border-left-color: ${COLORS.info}; }

                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: rgba(41, 90, 167, 0.1); color: ${COLORS.primary}; }
                .kpi-success .kpi-icon { background: rgba(16, 185, 129, 0.1); color: ${COLORS.success}; }
                .kpi-danger .kpi-icon { background: rgba(239, 68, 68, 0.1); color: ${COLORS.danger}; }
                .kpi-warning .kpi-icon { background: rgba(245, 158, 11, 0.1); color: ${COLORS.warning}; }
                .kpi-info .kpi-icon { background: rgba(59, 130, 246, 0.1); color: ${COLORS.info}; }

                .kpi-content { flex: 1; }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }

                /* Action icons */
                .action-icon {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    background: #f8f9fa;
                    border: 1px solid #e9ecef;
                    color: #6c757d;
                    transition: all 0.2s;
                    cursor: pointer;
                }
                .action-icon:hover {
                    background: ${COLORS.primary};
                    border-color: ${COLORS.primary};
                    color: white !important;
                }
                .action-icon-primary:hover {
                    background: ${COLORS.primary};
                    border-color: ${COLORS.primary};
                    color: white !important;
                }
                .action-icon-danger:hover {
                    background: #dc3545;
                    border-color: #dc3545;
                    color: white !important;
                }

                /* Référence */
                .reference-badge {
                    background: #f8f9fa;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                }

                .member-avatar {
                    width: 34px;
                    height: 34px;
                    border-radius: 8px;
                    background: #f1f4f9;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 600;
                    font-size: 0.8rem;
                    color: ${COLORS.primary};
                    border: 1px solid #e2e8f0;
                }

                .fs-20 { font-size: 1.25rem; }
                .fs-24 { font-size: 1.5rem; }
                .fs-48 { font-size: 3rem; }
                .fs-14 { font-size: 0.875rem; }

                .rounded-3 { border-radius: 0px !important; }
                .rounded-pill { border-radius: 50px !important; }

                .header-modern {
                    background: white;
                    padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid ${COLORS.secondary};
                    transition: all 0.2s ease;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                }
                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                }
                .header-icon-wrapper {
                    width: 48px;
                    height: 48px;
                    background: ${COLORS.primary};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 4px;
                    color: white;
                    font-size: 1.4rem;
                }
                .gradient-text {
                    color: #1e293b;
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0;
                }
                .badge-modern {
                    background: #f1f4f9;
                    color: #475569;
                    padding: 0.3rem 0.8rem;
                    border-radius: 4px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .badge-modern i {
                    color: ${COLORS.secondary};
                }
                .btn-modern-outline {
                    border: 1px solid #d1d5db;
                    background: white;
                    padding: 0.4rem 1.2rem;
                    border-radius: 4px;
                    font-size: 0.85rem;
                    font-weight: 500;
                    color: #4b5563;
                    transition: all 0.2s;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: ${COLORS.secondary};
                    color: ${COLORS.secondary};
                    background: #fafafa;
                }
                .btn-modern-primary {
                    background: ${COLORS.primary};
                    border: none;
                    padding: 0.4rem 1.2rem;
                    border-radius: 4px;
                    font-size: 0.85rem;
                    font-weight: 500;
                    color: white;
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    transition: all 0.2s;
                }
                .btn-modern-primary:hover {
                    background: ${COLORS.primaryDark};
                    box-shadow: 0 4px 10px rgba(41,90,167,0.3);
                }
                .fade-in {
                    animation: fadeIn 0.3s ease-out;
                }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(6px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </>
    );
}